import React, { useMemo, useState } from "react"
import { toast } from "react-toastify"
import clsx from "clsx"

import { useTimezone } from "hooks/useTimezone"

import { AffiliateCommissionMode, UpdateUserAffiliatePayload, UserAffiliate } from "services/user/type"
import { userService } from "services/user/user.service"

import { ModalEditCpaConfig } from "./modals/ModalEditCpaConfig"
import { UserAffiliateReferredUsersTable } from "./UserAffiliateReferredUsersTable"

type Props = {
  userId?: string
  affiliates: UserAffiliate[]
  onUpdated: () => void
}

const fmtMoney = (v: number) => {
  const n = Number(v ?? 0)
  return new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(n / 100)
}

const isValidPercent = (v: unknown) => {
  const n = Number(v)
  return !Number.isNaN(n) && n >= 0 && n <= 100
}

export const UserAffiliateTable = ({ userId, affiliates, onUpdated }: Props) => {
  const { formatDateTime } = useTimezone()
  const [editingId, setEditingId] = useState<string | null>(null)
  const [draftMode, setDraftMode] = useState<AffiliateCommissionMode | "">("")
  const [draftReturnPercent, setDraftReturnPercent] = useState("")
  const [savingId, setSavingId] = useState<string | null>(null)
  const [cpaEditing, setCpaEditing] = useState<UserAffiliate | null>(null)
  const [openRowId, setOpenRowId] = useState<string | null>(null)

  const rows = useMemo(() => affiliates ?? [], [affiliates])

  const startEdit = (row: UserAffiliate) => {
    setEditingId(row.id)
    setDraftMode(row.commission_mode ?? "")
    setDraftReturnPercent(row.return_percent === null || row.return_percent === undefined ? "" : String(row.return_percent))
  }

  const cancelEdit = () => {
    setEditingId(null)
    setDraftMode("")
    setDraftReturnPercent("")
    setSavingId(null)
  }

  const rowPercentError = useMemo(() => {
    if (!editingId) return null
    if (!draftReturnPercent.trim()) return null
    return isValidPercent(draftReturnPercent) ? null : "return_percent must be between 0 and 100"
  }, [editingId, draftReturnPercent])

  const buildPatch = (): UpdateUserAffiliatePayload => {
    const patch: UpdateUserAffiliatePayload = {}

    if (draftMode) patch.commission_mode = draftMode

    if (draftReturnPercent.trim() !== "") {
      patch.return_percent = Number(draftReturnPercent)
    }

    return patch
  }

  const canSave = useMemo(() => {
    if (!userId || !editingId) return false
    if (savingId) return false
    if (rowPercentError) return false

    const patch = buildPatch()
    return patch.commission_mode !== undefined || patch.return_percent !== undefined
  }, [userId, editingId, savingId, rowPercentError, draftMode, draftReturnPercent])

  const save = async () => {
    if (!userId || !editingId) return
    if (rowPercentError) return toast.error(rowPercentError)

    const patch = buildPatch()
    if (patch.commission_mode === undefined && patch.return_percent === undefined) {
      return toast.error("Nothing to update")
    }

    try {
      setSavingId(editingId)
      await userService.updateUserAffiliate(userId, editingId, patch)
      toast.success("Affiliate updated")
      cancelEdit()
      onUpdated()
    } catch (e: any) {
      toast.error(e?.data?.message ?? e?.message ?? "Failed to update affiliate")
      setSavingId(null)
    }
  }

  const onRowClick = (rowId: string) => {
    if (editingId) return
    setOpenRowId(openRowId === rowId ? null : rowId)
  }

  const cellBase = "py-3 px-3 text-sm text-[rgba(6,7,21,1)] dark:text-white"
  const cellMuted = "text-[rgba(6,7,21,0.7)] dark:text-white/70"
  const colsCount = 10

  return (
    <div className={clsx("rounded-xl border overflow-x-auto", "border-[rgba(0,0,0,0.05)] dark:border-[rgba(255,255,255,0.1)]")}>
      <div className={clsx("flex items-center justify-between gap-4 p-4", "bg-white dark:bg-[#0C0C1A]")}>
        <div className="text-base font-semibold text-[rgba(6,7,21,1)] dark:text-white">Affiliate codes</div>
        <div className="text-sm opacity-70">{rows.length} total</div>
      </div>

      <table className="w-full min-w-[1200px] bg-white dark:bg-[#0C0C1A]">
        <thead className="bg-[rgba(139,92,246,0.06)] dark:bg-[rgba(139,92,246,0.08)]">
          <tr className="text-xs uppercase tracking-wide text-[rgba(6,7,21,0.6)] dark:text-white/70">
            <th className="py-3 px-3 text-left">Code</th>
            <th className="py-3 px-3 text-left">Mode</th>
            <th className="py-3 px-3 text-left">Return %</th>
            <th className="py-3 px-3 text-left">Total refs</th>
            <th className="py-3 px-3 text-left">Balance</th>
            <th className="py-3 px-3 text-left">Total earned</th>
            <th className="py-3 px-3 text-left">Est. unreleased</th>
            <th className="py-3 px-3 text-left">Created</th>
            <th className="py-3 px-3 text-right">Actions</th>
          </tr>
        </thead>

        <tbody>
          {!rows.length ? (
            <tr>
              <td colSpan={colsCount} className="py-10 text-center">
                No affiliate codes for this user
              </td>
            </tr>
          ) : null}

          {rows.map((row) => {
            const rowId = String(row?.id ?? row?.code)
            const isOpen = openRowId === rowId
            const hasSelection = openRowId !== null

            const isEditing = editingId === row.id
            const isSaving = savingId === row.id

            const totalRefs = row?.total_refs ?? (Array.isArray(row?.referral_links) ? row.referral_links.length : 0)

            const commissionMode = (row?.commission_mode ?? "") as AffiliateCommissionMode | ""

            return (
              <React.Fragment key={rowId}>
                <tr
                  className={clsx(
                    "cursor-pointer border-t border-[rgba(0,0,0,0.05)] dark:border-[rgba(255,255,255,0.08)]",
                    !hasSelection &&
                      !editingId &&
                      "hover:contrast-125 hover:bg-[rgba(139,92,246,0.2)] hover:dark:bg-[rgba(139,92,246,0.20)]",
                    hasSelection && !isOpen && "opacity-40 hover:opacity-60",
                    hasSelection && isOpen && "bg-[rgba(139,92,246,0.06)] dark:bg-[rgba(139,92,246,0.10)]",
                    savingId && !isSaving && "opacity-60"
                  )}
                  onClick={() => onRowClick(rowId)}
                >
                  <td className={clsx(cellBase, "font-mono font-medium")}>{row?.code ?? "-"}</td>

                  <td className={clsx(cellBase, cellMuted)}>
                    {isEditing ? (
                      <select
                        className="select select-bordered select-sm w-full"
                        value={draftMode}
                        onChange={(e) => setDraftMode((e.target.value as AffiliateCommissionMode) || "")}
                        disabled={isSaving}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <option value="">Do not change</option>
                        <option value={AffiliateCommissionMode.RAKE}>RAKE</option>
                        <option value={AffiliateCommissionMode.NGR}>NGR</option>
                        <option value={AffiliateCommissionMode.CPA}>CPA</option>
                      </select>
                    ) : (
                      <span>{commissionMode || "—"}</span>
                    )}
                  </td>

                  <td className={clsx(cellBase, cellMuted)}>
                    {isEditing ? (
                      <div className="flex flex-col gap-1" onClick={(e) => e.stopPropagation()}>
                        <input
                          className={clsx("input input-bordered input-sm w-full", { "input-error": !!rowPercentError })}
                          value={draftReturnPercent}
                          onChange={(e) => setDraftReturnPercent(e.target.value)}
                          disabled={isSaving}
                          placeholder="0 - 100"
                          inputMode="decimal"
                        />
                        {rowPercentError ? <div className="text-xs text-error">{rowPercentError}</div> : null}
                      </div>
                    ) : (
                      <span>{row?.return_percent && commissionMode !== AffiliateCommissionMode.CPA ? row?.return_percent : "—"}</span>
                    )}
                  </td>

                  <td className={clsx(cellBase, cellMuted)}>{totalRefs}</td>

                  <td className={clsx(cellBase, cellMuted)}>{fmtMoney(row.balance)}</td>

                  <td className={clsx(cellBase, cellMuted)}>{fmtMoney(row.total_earned)}</td>

                  <td className={clsx(cellBase, cellMuted)}>
                    {row?.estimated_unreleased ? `${fmtMoney(row?.estimated_unreleased)} USD` : "-"}
                  </td>

                  <td className={clsx(cellBase, cellMuted)}>{row?.created_at ? formatDateTime(row?.created_at) : "-"}</td>

                  <td className={clsx(cellBase, "text-right")} onClick={(e) => e.stopPropagation()}>
                    {isEditing ? (
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          className={clsx("btn btn-ghost btn-sm", { "btn-disabled": isSaving })}
                          onClick={cancelEdit}
                          disabled={isSaving}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className={clsx("btn btn-primary btn-sm", { "btn-disabled": !canSave })}
                          onClick={save}
                          disabled={!canSave}
                        >
                          {isSaving ? <span className="loading loading-spinner loading-xs" /> : null}
                          Save
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          className={clsx("btn btn-ghost btn-sm", { "btn-disabled": !userId || !!savingId })}
                          onClick={() => startEdit(row)}
                          disabled={!userId || !!savingId}
                        >
                          Edit
                        </button>
                        {row.commission_mode === AffiliateCommissionMode.CPA && (
                          <button
                            type="button"
                            className={clsx("btn btn-ghost btn-sm", { "btn-disabled": !userId || !!savingId })}
                            onClick={() => setCpaEditing(row)}
                            disabled={!userId || !!savingId}
                          >
                            CPA config
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>

                {isOpen ? (
                  <tr className="border-t border-[rgba(0,0,0,0.05)] dark:border-[rgba(255,255,255,0.08)]">
                    <td colSpan={colsCount} className="p-0">
                      <div
                        className={clsx(
                          "bg-[rgba(0,0,0,0.01)] dark:bg-white/5",
                          "border-t border-[rgba(0,0,0,0.05)] dark:border-[rgba(255,255,255,0.08)]"
                        )}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <UserAffiliateReferredUsersTable links={row?.referral_links ?? []} />
                      </div>
                    </td>
                  </tr>
                ) : null}
              </React.Fragment>
            )
          })}
        </tbody>
      </table>
      {userId && cpaEditing ? (
        <ModalEditCpaConfig
          key={`cpa-edit-${cpaEditing.id}`}
          open={!!cpaEditing}
          onClose={() => setCpaEditing(null)}
          userId={userId}
          affiliate={cpaEditing}
          onUpdated={onUpdated}
        />
      ) : null}
    </div>
  )
}
